import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { IconDateRange, IconDirectionsBus, IconDirectionsCar, IconDirectionsRailway, IconHotel, IconLuggage, IconNoMeals, IconNotInterested, IconNoTransfer, IconRestaurant } from '@travel-frontend/icons';
import { useTranslation } from 'next-i18next';
import { Container } from '../../components/HotelServices/components/Container.js';
import { Divider } from '../../components/HotelServices/components/Divider.js';
import { Luggage } from '../../components/HotelServices/components/Luggage.js';
import { LuggageList } from '../../components/HotelServices/components/LuggageList.js';
import { Specials } from '../../components/HotelServices/components/Specials/index.js';
import { InviaTypeIcon } from '../../components/InviaTypeIcon/index.js';
import { HeadlineLarge, LabelMediumBold, LabelMediumSemiBold, List, ListItem, ParagraphMedium, Stack } from '../../primitives/index.js';
import { qaId } from '../../utils/index.js';
import { dateWithWeekday } from '../../utils/date.js';
import { OFFER_DETAILS_QA_ID } from '../../utils/qaId.js';
import { SPACING_MEDIUM, SPACING_SMALL, SPACING_XSMALL } from '../../utils/spacingLegacy.js';
const getMealIcon = cateringCode => {
  return cateringCode?.startsWith('IT06-NB') ? _jsx(IconNoMeals, {}) : _jsx(IconRestaurant, {});
};
export const HotelServices = ({
  services: {
    travelType,
    duration,
    overnights,
    departureDate,
    returnDate,
    room,
    roomDescription,
    roomAttributes: rawRoomAttributes,
    multiRooms,
    includedServices,
    oceanViewInfo,
    oceanViewDescription,
    baggageIncluded,
    flightsLuggage,
    flightsHandLuggage,
    specialInfo,
    cancellationInfo = [],
    ...restServices
  }
}) => {
  const {
    t
  } = useTranslation('hotelServices');
  const ownArrival = travelType === 'ownArrival';
  const transferInclusive = includedServices?.includes('transfer');
  const trainToAirport = includedServices?.includes('rail_and_fly');
  const rentalCarInclusive = includedServices?.includes('rental_car');
  const singleRoom = !multiRooms || multiRooms?.parsedRooms?.length === 0 || multiRooms?.parsedRooms?.length === 1 && multiRooms?.parsedRooms?.[0].roomCount === 1;
  const luggageWeight = flightsLuggage != null && flightsLuggage.weight > 0 ? flightsLuggage.weight : 0;
  const handLuggageWeight = flightsHandLuggage != null && flightsHandLuggage.weight > 0 ? flightsHandLuggage.weight : 0;
  const hasRoomDescription = roomDescription && roomDescription.toLowerCase() !== room.toLowerCase();
  const hasSpecial = specialInfo.some(special => !!special.inviaType);
  const hasCancellationInfo = cancellationInfo.some(cancellation => !!cancellation.inviaType);
  const roomAttributes = Array.isArray(rawRoomAttributes) ? rawRoomAttributes.map(({
    type,
    name
  }) => ({
    type,
    label: name,
    icon: _jsx(InviaTypeIcon, {
      inviaType: type
    }),
    description: ''
  })) : oceanViewInfo ? [{
    type: 'IT13-SEVI#SEVI',
    label: oceanViewInfo,
    icon: _jsx(InviaTypeIcon, {
      inviaType: "IT13-SEVI#SEVI"
    }),
    description: oceanViewDescription || ''
  }] : [];
  return _jsx(Container, {
    children: _jsxs(Stack, {
      children: [_jsx(HeadlineLarge, {
        children: t('title')
      }), _jsxs(List, {
        spacing: SPACING_MEDIUM,
        children: [_jsx(ListItem, {
          "data-testid": qaId(OFFER_DETAILS_QA_ID, 'trip-duration'),
          size: "large",
          icon: _jsx(IconDateRange, {}),
          label: _jsxs(Stack, {
            direction: "row",
            spacing: SPACING_SMALL,
            children: [_jsx(LabelMediumBold, {
              children: t('day', {
                count: duration
              })
            }), overnights > 0 && _jsxs(_Fragment, {
              children: [_jsx(Divider, {}), _jsx(LabelMediumBold, {
                children: t('night', {
                  count: overnights
                })
              })]
            })]
          }),
          children: _jsxs(ParagraphMedium, {
            color: "contentSecondary",
            "data-testid": qaId(OFFER_DETAILS_QA_ID, 'trip-dates'),
            children: [dateWithWeekday(departureDate), " - ", dateWithWeekday(returnDate)]
          })
        }), singleRoom && _jsxs(_Fragment, {
          children: [_jsx(ListItem, {
            size: "large",
            icon: _jsx(IconHotel, {}),
            label: room,
            children: hasRoomDescription && _jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: roomDescription
            })
          }), roomAttributes.map(({
            type,
            label,
            icon,
            description
          }) => _jsx(ListItem, {
            size: "large",
            icon: icon,
            label: label,
            children: description && _jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: description
            })
          }, type))]
        }), !singleRoom && _jsxs(_Fragment, {
          children: [multiRooms?.parsedRooms.map(({
            roomCount,
            roomType,
            description,
            roomAttributes,
            ...restRoomData
          }, index) => _jsxs(ListItem, {
            "data-testid": qaId(OFFER_DETAILS_QA_ID, 'room-type'),
            size: "large",
            icon: _jsx(IconHotel, {}),
            label: _jsx("span", {
              "data-testid": qaId(OFFER_DETAILS_QA_ID, 'room-type-short'),
              children: `${roomCount}× ${roomType}`
            }),
            children: [description && _jsx(ParagraphMedium, {
              color: "contentSecondary",
              "data-testid": qaId(OFFER_DETAILS_QA_ID, 'room-type-long'),
              children: description
            }), _jsxs(List, {
              spacing: SPACING_XSMALL,
              style: {
                marginTop: '0.5rem'
              },
              children: [restRoomData.catering && _jsx(ListItem, {
                size: "medium",
                icon: getMealIcon(restRoomData.cateringCode),
                label: _jsx(LabelMediumSemiBold, {
                  children: restRoomData.catering
                }),
                children: restRoomData.cateringDescription && _jsx(ParagraphMedium, {
                  color: "contentSecondary",
                  children: restRoomData.cateringDescription
                })
              }), roomAttributes
              // global attributes are rendered once below
              .filter(({
                type
              }) => !multiRooms?.globalAttributes.some(globalAttribute => globalAttribute.type === type)).map(({
                type,
                label,
                description
              }) => _jsx(ListItem, {
                size: "medium",
                icon: _jsx(InviaTypeIcon, {
                  inviaType: type
                }),
                label: _jsx(LabelMediumSemiBold, {
                  "data-testid": qaId(OFFER_DETAILS_QA_ID, `room-type-${type}-attribute`),
                  children: label
                }),
                children: description && _jsx(ParagraphMedium, {
                  color: "contentSecondary",
                  children: description
                })
              }, type))]
            })]
          }, index)), multiRooms?.globalAttributes.map(({
            type,
            label,
            description
          }) => _jsx(ListItem, {
            "data-testid": qaId(OFFER_DETAILS_QA_ID, `room-${type}-attribute`),
            size: "large",
            icon: _jsx(InviaTypeIcon, {
              inviaType: type
            }),
            label: t('globalRoomAttributeLabel', {
              attribute: label
            }),
            children: description && _jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: description
            })
          }, type))]
        }), restServices.catering && _jsx(ListItem, {
          "data-testid": qaId(OFFER_DETAILS_QA_ID, 'board-type'),
          size: "large",
          icon: getMealIcon(restServices.cateringCode),
          label: restServices.catering,
          children: restServices.cateringDescription && _jsx(ParagraphMedium, {
            color: "contentSecondary",
            children: restServices.cateringDescription
          })
        }), !ownArrival && _jsxs(_Fragment, {
          children: [_jsx(ListItem, {
            "data-testid": qaId(OFFER_DETAILS_QA_ID, 'transfer'),
            size: "large",
            icon: transferInclusive ? _jsx(IconDirectionsBus, {}) : _jsx(IconNoTransfer, {}),
            label: t('transfer', {
              context: transferInclusive ? 'inclusive' : 'not_inclusive'
            }),
            children: transferInclusive && _jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: t('transfer_inclusive_description')
            })
          }), rentalCarInclusive && _jsx(ListItem, {
            "data-testid": qaId(OFFER_DETAILS_QA_ID, 'rentalCar'),
            size: "large",
            icon: _jsx(IconDirectionsCar, {}),
            label: t('rentalCar'),
            children: _jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: t('rentalCarDescription')
            })
          }), trainToAirport && _jsx(ListItem, {
            "data-testid": qaId(OFFER_DETAILS_QA_ID, 'railAndFly'),
            size: "large",
            icon: _jsx(IconDirectionsRailway, {}),
            label: t('railAndFly'),
            children: _jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: t('railAndFlyDescription')
            })
          })]
        }), !ownArrival && _jsx(ListItem, {
          "data-testid": qaId(OFFER_DETAILS_QA_ID, 'baggageInfo'),
          size: "large",
          icon: baggageIncluded ? _jsx(IconLuggage, {}) : _jsx(IconNotInterested, {}),
          label: t('baggage', {
            context: baggageIncluded ? 'inclusive' : 'not_inclusive'
          }),
          children: _jsxs(Stack, {
            spacing: SPACING_SMALL,
            children: [_jsx(ParagraphMedium, {
              color: "contentSecondary",
              children: _jsx(Luggage, {
                children: _jsx(LuggageList, {
                  luggageAmount: flightsLuggage ? 1 : 0,
                  handLuggageAmount: flightsHandLuggage ? 1 : 0,
                  luggageWeight: luggageWeight,
                  handLuggageWeight: handLuggageWeight
                })
              })
            }), _jsx(ParagraphMedium, {
              color: "contentSecondary",
              "data-testid": qaId(OFFER_DETAILS_QA_ID, 'baggageInfo-description'),
              children: t(flightsLuggage ? 'luggageTooltip.legalNotice' : 'luggageTooltip.missingLuggageNotice')
            })]
          })
        })]
      }), hasSpecial && _jsx(Specials, {
        specialInfo: specialInfo
      }), hasCancellationInfo && _jsx(Specials, {
        specialInfo: cancellationInfo
      })]
    })
  });
};